<template>
  <div>
    <h3>Application Progress</h3>

    <b-alert variant="info" style="font-weight:bold;" dismissible fade show>
      Please note: You must select a term to view student opportunities for it
    </b-alert>

    <b-form inline @submit.prevent="" class="mb-2">
      <label for="terms">Term</label>
      <b-form-select id="terms" @input="changeTerm" v-model="term" :options="terms" value-field="termId"
        text-field="description">
        <template slot="first">
          <!-- this slot appears above the options from 'options' prop -->
          <option :value="null">None</option>
        </template>
      </b-form-select>

      <label for="types">Type</label>
      <b-form-select id="types" v-model="opportunityTypeId" :options="opportunityTypes" value-field="opportunityTypeId"
        text-field="name">
        <template slot="first">
          <!-- this slot appears above the options from 'options' prop -->
          <option :value="null">All</option>
        </template>
      </b-form-select>

      <label for="categories">Category</label>
      <b-form-select id="categories" v-model="opportunityCategoryId" :options="validCategoryOptions"
        value-field="opportunityCategoryId" text-field="name">
        <template slot="first">
          <!-- this slot appears above the options from 'options' prop -->
          <option :value="null">All</option>
        </template>
      </b-form-select>

      <label for="courses">Course</label>
      <b-form-select id="courses" v-model="course" :options="courses" value-field="displayName"
        text-field="displayName">
        <template slot="first">
          <!-- this slot appears above the options from 'options' prop -->
          <option :value="null">All</option>
        </template>
      </b-form-select>

      <b-input-group class="filter-group ml-2">
        <b-input-group-prepend is-text>
          <font-awesome-icon icon="search" />
        </b-input-group-prepend>
        <b-form-input v-model="filter" placeholder="Type to Search"></b-form-input>
        <b-input-group-append>
          <b-button @click="filter = null">Clear</b-button>
        </b-input-group-append>
      </b-input-group>
      <b-input-group class="filter-group ml-2">
        <b-form-checkbox v-for="status in additionalMaterialStatuses"
          :key="status.studentOpportunityAdditionalMaterialStatusDefinitionId" v-model="statuses"
          :value="status.studentOpportunityAdditionalMaterialStatusDefinitionId">
          {{ status.name }}
        </b-form-checkbox>
      </b-input-group>
    </b-form>

    <div>
      <a :href="csvUrl">Download CSV</a>
    </div>


    <b-table striped hover :items="studentOpportunities" :fields="fields" :per-page="perPage"
      :current-page="currentPage" :filter="filterItems" @filtered="onFiltered" responsive :sort-compare="customSort">
      <template slot="name" slot-scope="data">
        {{ data.item.opportunity.name }}
      </template>
      <template slot="type" slot-scope="data">
        {{ data.item.opportunity.opportunityType ? data.item.opportunity.opportunityType.name : "" }}
        <div>{{ data.item.opportunity.opportunityCategory ? "(" + data.item.opportunity.opportunityCategory.name + ")" :
      "" }}
        </div>
      </template>
      <template slot="site" slot-scope="data">
        {{ data.item.opportunity.site ? data.item.opportunity.site.name : "" }}
        <div v-if="data.item.opportunity.opportunityType.name == 'Translational Team'">
          {{ data.item.opportunity.translationalTeam ? "(" + data.item.opportunity.translationalTeam.name + ")" : "" }}
        </div>
      </template>
      <template slot="course" slot-scope="data">
        <div v-if="data.item.studentView">
          <div v-for="course in data.item.studentView.studentCourses" :key="course.classSid">
            <p>
              {{ course.subjectCode }} {{ course.catalogNumber }}
            </p>
            <p>{{ course.unitsTaken }} credits</p>
          </div>
        </div>
      </template>
      <template slot="additionalMaterials" slot-scope="data">
        <b-list-group>
          <b-list-group-item
            v-for="studentOpportunityAdditionalMaterial in data.item.studentOpportunityAdditionalMaterials"
            :key="studentOpportunityAdditionalMaterial.studentOpportunityAdditionalMaterialId">
            <a v-if="studentOpportunityAdditionalMaterial.studentOpportunityAdditionalMaterialFiles.length"
              :href="getAdditionalMaterialFileHref(studentOpportunityAdditionalMaterial.studentOpportunityAdditionalMaterialFiles)">
              {{ studentOpportunityAdditionalMaterial.additionalMaterialType.name }} <br />
              (
              {{
      studentOpportunityAdditionalMaterial.currentStatus.studentOpportunityAdditionalMaterialStatusDefinition.name
    }}
              - {{ studentOpportunityAdditionalMaterial.currentStatus.createdBy }}
              - {{ studentOpportunityAdditionalMaterial.currentStatus.createdDate | formatDate }}
              )
            </a>
            <span v-else>
              {{ studentOpportunityAdditionalMaterial.additionalMaterialType.name }} <br />
              (
              {{
      studentOpportunityAdditionalMaterial.currentStatus.studentOpportunityAdditionalMaterialStatusDefinition.name
    }}
              - {{ studentOpportunityAdditionalMaterial.currentStatus.createdBy }}
              - {{ studentOpportunityAdditionalMaterial.currentStatus.createdDate | formatDate }}
              )
            </span>
          </b-list-group-item>
        </b-list-group>
      </template>
      <template slot="completion" slot-scope="data">
        <div v-if="data.item.minRequiredStatus">
          <span :class="{
      'green': data.item.minRequiredStatus.studentOpportunityAdditionalMaterialStatusDefinition.name === 'Approved',
      'red': data.item.minRequiredStatus.studentOpportunityAdditionalMaterialStatusDefinition.name === 'Not Submitted',
      'orange': data.item.minRequiredStatus.studentOpportunityAdditionalMaterialStatusDefinition.name !== 'Approved'
        && data.item.minRequiredStatus.studentOpportunityAdditionalMaterialStatusDefinition.name != 'Not Submitted'
    }">
            <font-awesome-icon icon="check"
              v-if="getCompletionPercentage(data.item.studentOpportunityAdditionalMaterials) == 100" />
            <font-awesome-icon icon="times"
              v-if="!getCompletionPercentage(data.item.studentOpportunityAdditionalMaterials)" />
            {{ getCompletionPercentage(data.item.studentOpportunityAdditionalMaterials) | number(2) }}%
          </span>
        </div>
      </template>
      <template slot="maxDateSubmitted" slot-scope="data">
        <span>{{ data.item.maxDateSubmitted | formatDate }}</span>
      </template>
      <template slot="details" slot-scope="data">
        <router-link
          :to="{ name: 'studentOpportunityDetails', params: { studentOpportunityId: data.item.studentOpportunityId } }">View</router-link>
      </template>
    </b-table>

    <b-pagination :total-rows="totalRows" v-model="currentPage" :per-page="perPage" align="right" />
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import terms from "../../api/terms";
import opportunities from "../../api/opportunity";
import studentOpportunities from "../../api/studentOpportunity";
import * as _ from "lodash"; // eslint-disable-line no-unused-vars

export default {
  data() {
    return {
      fields: [
        { key: "name", label: "Name / Title", sortable: true },
        { key: "studentView.lastName", label: "Last Name", sortable: true },
        { key: "studentView.firstName", label: "First Name", sortable: true },
        { key: "course", sortable: true, label: "Enrolled Course" },
        { key: "type", sortable: true, label: "Type / Category" },
        { key: "site", label: "Site", sortable: true },
        { key: "opportunity.term.description", label: "Term", sortable: true },
        { key: "additionalMaterials", sortable: false },
        {
          key: "completion",
          sortable: true,
          label: "Completion of Required Items"
        },
        {
          key: "otherStudentOpportunitiesCount",
          label: "# Others Saved",
          sortable: true
        },
        {
          key: "otherStudentOpportunitiesWithSubmissions",
          label: "# Others w/ Submissions",
          sortable: true
        },
        {
          key: "otherStudentOpportunitiesApproved",
          label: "# Others Approved",
          sortable: true
        },
        { key: "maxDateSubmitted", label: "Submitted On", sortable: true },
        { key: "details", sortable: false }
      ],
      currentPage: 1,
      perPage: 10,
      totalRows: 0,
      filter: null,
      term: null,
      terms: [],
      opportunityTypeId: null,
      opportunityCategoryId: null,
      opportunityCategories: [],
      statuses: [],
      course: null,
      courses: []
    };
  },

  computed: {
    ...mapState({
      studentOpportunities: state =>
        state.studentOpportunities.studentOpportunities,
      //terms: state => state.terms.terms,
      additionalMaterialStatuses: state =>
        state.studentOpportunities.additionalMaterialStatuses,
      opportunityTypes: state => state.studentOpportunities.opportunityTypes
    }),
    validCategoryOptions: function () {
      var that = this;
      if (this.opportunityTypeId) {
        return _.filter(this.opportunityCategories, function (category) {
          return _.find(category.opportunityTypeOpportunityCategories, function (
            opportunityTypeOpportunityCategory
          ) {
            return (
              opportunityTypeOpportunityCategory.opportunityTypeId ===
              that.opportunityTypeId
            );
          });
        });
      }
      return this.opportunityCategories;
    },
    csvUrl() {
      return (
        process.env.VUE_APP_BASE_URI +
        "/api/StudentOpportunities/getApplicationStatusCsv?termid=" +
        this.term
      );
    }
  },

  methods: {
    //...mapActions("terms", ["getTermsActive"]),
    ...mapActions("studentOpportunities", [
      "getAdditionalMaterialStatuses",
      "getOpportunityTypes",
      "getApplicationStatus"
    ]),
    changeTerm() {
      this.getApplicationStatus(this.term);
    },

    getAdditionalMaterialFileHref(fileList) {
      var file = fileList[fileList.length - 1];
      return (
        process.env.VUE_APP_BASE_URI +
        "/api/Downloads/StudentAdditionalMaterialFile?id=" +
        file.studentOpportunityAdditionalMaterialFileId
      );
    },

    getCompletionPercentage(studentOpportunityAdditionalMaterials) {
      var totalMaterials = 0;
      var approvedCount = 0;
      _.forEach(
        studentOpportunityAdditionalMaterials,
        studentOpportunityAdditionalMaterial => {
          if (
            studentOpportunityAdditionalMaterial.additionalMaterialType.required
          ) {
            totalMaterials += 1;
            if (
              studentOpportunityAdditionalMaterial.currentStatus
                .studentOpportunityAdditionalMaterialStatusDefinition.name ===
              "Approved"
            ) {
              approvedCount += 1;
            }
          }
        }
      );
      return (approvedCount / totalMaterials) * 100;
    },

    filterItems(item) {
      var matchedTerm = this.matchTerm(item, this.term);
      var matchedType = this.matchType(item, this.opportunityTypeId);
      var matchedCategory = this.matchCategory(
        item,
        this.opportunityCategoryId
      );
      var matchedCourse = this.matchCourse(item, this.course);
      var matchedFilter = this.matchFilter(item, this.filter);
      var matchedStatus = this.statuses.length
        ? this.matchStatus(item, this.statuses)
        : true;

      if (
        matchedTerm &&
        matchedType &&
        matchedCategory &&
        matchedCourse &&
        matchedFilter &&
        matchedStatus
      ) {
        return true;
      }
      return false;
    },
    matchTerm(item, term) {
      if (item.opportunity.term.termId === term || term === null) {
        return true;
      }
      return false;
    },
    matchType(item, opportunityTypeId) {
      if (
        opportunityTypeId === null ||
        item.opportunity.opportunityType.opportunityTypeId === opportunityTypeId
      ) {
        return true;
      }
      return false;
    },
    matchCategory(item, opportunityCategoryId) {
      if (
        opportunityCategoryId === null ||
        item.opportunity.opportunityCategory.opportunityCategoryId ===
        opportunityCategoryId
      ) {
        return true;
      }
      return false;
    },
    matchCourse(item, course) {
      if (
        course === null ||
        (item.studentView &&
          _.filter(item.studentView.studentCourses, studentCourse => {
            return (
              course ===
              studentCourse.subjectCode + " " + studentCourse.catalogNumber
            );
          }).length)
      ) {
        return true;
      }
      return false;
    },
    matchFilter(item, filter) {
      if (filter != "" && filter != null) {
        var found = this.search([item], this.filter);
        return found.length > 0;
      }
      return true;
    },
    matchStatus(item, statuses) {
      var minRequiredStatus = item.minRequiredStatus
        ? item.minRequiredStatus
          .studentOpportunityAdditionalMaterialStatusDefinition
          .studentOpportunityAdditionalMaterialStatusDefinitionId
        : null;
      var results = _.includes(statuses, minRequiredStatus);
      return results;
    },
    search(collection, text) {
      var that = this;
      return _.filter(collection, function (object) {
        var search = that.deepSearch(object, text.toLowerCase());
        return _.keys(search).length > 0;
      });
    },
    deepSearch(collection, predicate, thisArg) {
      if (_.isFunction(predicate)) {
        predicate = _.iteratee(predicate, thisArg);
      } else {
        var keys = _.flatten(_.tail(arguments));
        if (typeof keys === "string") {
          keys = keys.toLowerCase();
        }
        predicate = function (val, key) {
          if (typeof val === "string") {
            val = val.toLowerCase();
          }
          return _.includes(val, keys);
        };
      }
      var that = this;
      return _.transform(collection, function (memo, val, key) {
        var include = predicate(val, key);
        if (!include && _.isObject(val)) {
          val = that.deepSearch(val, predicate);
          include = !_.isEmpty(val);
        }
        if (include) {
          _.isArray(collection) ? memo.push(val) : (memo[key] = val);
        }
      });
    },

    onFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },

    customSort(a, b, key) {
      if (key === "name") {
        return a.opportunity.name.localeCompare(b.opportunity.name, undefined, {
          numeric: true
        });
      } else if (key === "studentView.lastName") {
        return a.studentView.lastName.localeCompare(
          b.studentView.lastName,
          undefined,
          {
            numeric: true
          }
        );
      } else if (key === "studentView.firstName") {
        return a.studentView.firstName.localeCompare(
          b.studentView.firstName,
          undefined,
          { numeric: true }
        );
      } else if (key === "course") {
        var aCourse = a.studentView.studentCourses[0]
          ? a.studentView.studentCourses[0].subjectCode +
          " " +
          a.studentView.studentCourses[0].catalogNumber
          : "";
        var bCourse = b.studentView.studentCourses[0]
          ? b.studentView.studentCourses[0].subjectCode +
          " " +
          b.studentView.studentCourses[0].catalogNumber
          : "";
        return aCourse.localeCompare(bCourse, undefined, { numeric: true });
      } else if (key === "category") {
        var categoryNameA = a.opportunity.opportunityCategory
          ? a.opportunity.opportunityCategory.name
          : "";
        var categoryNameB = b.opportunity.opportunityCategory
          ? b.opportunity.opportunityCategory.name
          : "";
        return categoryNameA.localeCompare(categoryNameB, undefined, {
          numeric: true
        });
      } else if (key === "type") {
        var typeNameA = a.opportunity.opportunityType
          ? a.opportunity.opportunityType.name
          : "";
        var typeNameB = b.opportunity.opportunityType
          ? b.opportunity.opportunityType.name
          : "";
        return typeNameA.localeCompare(typeNameB, undefined, { numeric: true });
      } else if (key === "site") {
        return a.opportunity.site.name.localeCompare(
          b.opportunity.site.name,
          undefined,
          { numeric: true }
        );
      } else if (key === "opportunity.term.description") {
        return a.opportunity.term.termCode < b.opportunity.term.termCode
          ? -1
          : a.opportunity.term.termCode > b.opportunity.term.termCode
            ? 1
            : 0;
      } else if (key === "completion") {
        var completionA = this.getCompletionPercentage(
          a.studentOpportunityAdditionalMaterials
        );
        var completionB = this.getCompletionPercentage(
          b.studentOpportunityAdditionalMaterials
        );
        return completionA < completionB
          ? -1
          : completionA > completionB
            ? 1
            : 0;
      }
      return null;
    }
  },

  watch: {
    terms: function () {
      if (this.term === null) {
        //this.term = this.terms[0].termId;
        var foundTerm = _.find(this.terms, t => {
          var start = new Date(t.termStart);
          var end = new Date(t.termEnd);
          return start.getTime() <= Date.now() && end.getTime() >= Date.now();
        });
        if (foundTerm) {
          this.term = foundTerm.termId;
        }
      }
    }
  },
  created() {
    if (this.term) {
      this.getApplicationStatus(this.term);
    }
    terms.getTerms().then(response => {
      this.terms = response.data;
    });
    opportunities.getInternshipCourses().then(response => {
      this.courses = response.data;
    });
    opportunities.getOpportunityCategories().then(response => {
      this.opportunityCategories = response.data;
    });
    this.getAdditionalMaterialStatuses();
    this.getOpportunityTypes();
  }
};
</script>

<style scoped>
.green {
  color: green;
}

.red {
  color: red;
}

.orange {
  color: orange;
}

.main-container {
  max-width: 100%;
  margin-top: 10px;
}

.widget-body {
  max-width: 100%;
}

/* .filter-group {
  margin: 10px 0 15px 0;
  max-width: 600px;
} */

.new-content-button {
  margin-left: 10px;
}

label {
  font-weight: bold;
  margin: 0 0.3em 0 1.5em;
}

.clickable {
  cursor: pointer;
}

.sort-label {
  margin-right: 0.5em;
}

.loading-div {
  text-align: center;
  font-size: 2em;
}
</style>
